import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { fadeInUpAnimation } from "src/@fury/animations/fade-in-up.animation";
import { fadeInRightAnimation } from "src/@fury/animations/fade-in-right.animation";
import { scaleInAnimation } from "src/@fury/animations/scale-in.animation";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { SelectionModel } from "@angular/cdk/collections";
import { ApiService } from "src/app/services/api.service";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import endpoints from "src/app/helpers/endpoints";
import { Subject } from "rxjs";
import { ErrorType } from "src/app/enums/error-type.enum";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { StorageService } from "src/app/services/storage.service";
import { CommonService } from "src/app/services/common.service";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";

@Component({
  selector: "fury-manage-optional-item-modal",
  templateUrl: "./manage-optional-item-modal.component.html",
  styleUrls: ["./manage-optional-item-modal.component.scss"],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation],
})
export class ManageOptionalItemModalComponent implements OnInit, OnDestroy {
  itemList: any;
  selection = new SelectionModel<any>(true, []);
  loading: boolean = false;
  totalPrice = 0;
  ngUnSubscribe = new Subject<void>();
  expiryYear: number = Number(new Date().getFullYear().toString().slice(2, 4));
  month = Number(new Date().getMonth() + 1);
  cardDetailsForm = new FormGroup({
    cardHoldarName: new FormControl("", [Validators.required]),
    cardNumber: new FormControl("", [Validators.required]),
    expiryMonth: new FormControl("", [
      Validators.required,
      Validators.max(12),
      Validators.min(1),
    ]),
    expiryYear: new FormControl("", [
      Validators.required,
      Validators.min(this.expiryYear),
    ]),
    cvv: new FormControl("", [Validators.required]),
    transactionNumber: new FormControl(""),
    monthYear: new FormControl("", [Validators.required]),
  });
  bankDetailsForm = new FormGroup({
    routingNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(9),
      Validators.maxLength(9),
    ]),
    accountNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(4),
    ]),
    nameOnAccount: new FormControl("", [
      Validators.minLength(2),
      Validators.required,
    ]),
    bankName: new FormControl("", [Validators.required]),
    transactionNumber: new FormControl("", [Validators.required]),
  });
  chequeDetailForm = new FormGroup({
    checkNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(4),
    ]),
  });
  agentId = this.storageService.getUser()?.userId;
  paymentTypeEnum = {
    0: "credit-debit-card",
    1: "bank-payment",
    2: "check-payment",
  };
  paymentType = this.paymentTypeEnum[0];
  bankandChequePaymentVisible = true;
  cardType = "";
  step = 1;
  defaultCard: any;
  refundToDefaultCard = false;
  assetsUrl = environment.assetsUrl + "images/";

  constructor(
    private apiService: ApiService,
    public dialogRef: MatDialogRef<ManageOptionalItemModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { agreementId: string; userId: string },
    private storageService: StorageService,
    public commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.getOptionalItems();
    this.cardDetailsForm
      .get("monthYear")
      ?.valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((res: string) => {
        if (res) {
          if (res.length === 4) {
            this.cardDetailsForm.patchValue({
              expiryMonth: res.substring(0, 2),
              expiryYear: res.substring(2, 4),
            });
            if (
              this.cardDetailsForm.get("expiryYear").value ===
                this.expiryYear.toString() &&
              Number(this.cardDetailsForm.get("expiryMonth").value) < this.month
            ) {
              this.cardDetailsForm
                .get("expiryMonth")
                .setErrors({ notValid: true });
            }
          }
        }
      });
  }
  close() {
    this.dialogRef.close();
  }

  getOptionalItems() {
    this.loading = true;
    this.apiService
      .get(
        endpoints.customerDashboard.getAgreementOptionalItems(
          this.data.agreementId
        )
      )
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe(
        (res: HttpResponse<any>) => {
          if (res.status === 200) {
            this.itemList = res.body?.optionalCoveredItems;
            // select exist optional items
            this.itemList?.forEach((val: any) => {
              res.body?.agreementOptionalCoveredItems?.forEach(
                (element: any) => {
                  if (element?.covered_item_id === val["planCoverItemId"]) {
                    this.selection.select(val);
                  }
                }
              );
            });
            this.loading = false;
          }
        },
        (err: HttpErrorResponse) => {
          this.loading = false;
        }
      );
  }

  isAllSelected() {
    const numSelected = this.selection?.selected?.length;
    const numRows = this.itemList?.length;
    return numSelected === numRows;
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? "select" : "deselect"} all`;
    }
    return `${this.selection?.isSelected(row) ? "deselect" : "select"} row ${
      row.position + 1
    }`;
  }

  saveOptionalItems(paymentPayload?: any) {
    this.loading = true;
    const coverItemIds = this.selection.selected.map(
      (item: any) => item?.planCoverItemId
    );
    let payload = {
      coveredItemId: coverItemIds,
      amount: Number(this.totalPrice.toFixed(2)),
      isZeroAmount: this.totalPrice === 0,
      ...(this.totalPrice !== 0 ? paymentPayload : {}),
    };
    this.apiService
      .patch(
        endpoints.customerDashboard.updateAgreementOptionalItems(
          this.data.agreementId
        ),
        payload
      )
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.status === 200) {
            this.loading = false;
            this.apiService.alert(res.body.message, ErrorType.Success);
            this.selection.clear();
            this.dialogRef.close(res.body);
          } else {
            this.apiService.alert(res.body.message, ErrorType.Warning);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err) {
            this.loading = false;
            this.apiService.alert(err.message, ErrorType.Error);
          }
        },
      });
  }
  changeOptionalItems(event: any, item: any) {
    this.selection.toggle(item);

    if (!!event?.target?.checked) {
      this.totalPrice = this.totalPrice + Number(item?.standardPrice);
      return;
    }
    this.totalPrice = this.totalPrice - Number(item?.standardPrice);
  }

  cancel(): void {
    this.dialogRef.close();
  }
  getUserDefaultPaymentDetails() {
    this.apiService
      .get(endpoints.customerDashboard.getUserDefaultPayment(this.data.userId))
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.defaultCard = res.body?.defaultPaymentDetail;
            this.cardDetailsForm.patchValue({
              cardHoldarName: this.defaultCard.cardHolderName,
              cardNumber: this.defaultCard?.cardNumber,
              cvv: this.defaultCard?.cvv,
              // expiryMonth: this.defaultCard?.expiryMonth,
              // expiryYear: this.defaultCard?.expiryYear,
              monthYear: `${
                this.defaultCard?.expiryMonth
              }${this.defaultCard?.expiryYear.substring(2, 4)}`,
            });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.apiService.alert(error.message, ErrorType.Error);
        },
      });
  }
  getRefundableType() {
    this.apiService
      .get(
        endpoints.customerDashboard.getAgreementRefundableAction(
          this.data.agreementId,
          this.totalPrice
        )
      )
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.refundToDefaultCard =
              res.body?.refundableToOriginalPaymentSource;
            this.step = 2;
            if (this.refundToDefaultCard) {
              this.getUserDefaultPaymentDetails();
            }
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.message, ErrorType.Error);
        },
      });
  }
  detectCardType(cardNumber: string) {
    this.cardType = this.commonService.getCardType(cardNumber);
  }
  onTabChange(event: MatTabChangeEvent) {
    this.paymentType = this.paymentTypeEnum[event.index];
  }
  checkAmountAndProced() {
    if (this.totalPrice === 0) {
      this.saveOptionalItems();
      return;
    }
    this.getRefundableType();
  }
  checkPaymentDetails() {
    let payload: any;
    if (this.paymentType === this.paymentTypeEnum[0]) {
      if (this.cardDetailsForm.invalid) {
        this.cardDetailsForm.markAllAsTouched();
        return;
      }
      payload = {
        paymentDetail: {
          cardNumber: this.cardDetailsForm.value.cardNumber,
          cardHoldarName: this.cardDetailsForm.value.cardHoldarName,
          cvv: this.cardDetailsForm.value.cvv,
          expiryMonth: this.cardDetailsForm.value.expiryMonth,
          expiryYear: `20${this.cardDetailsForm.value.expiryYear}`,
        },
        paymentMethod: "credit-debit-card",
      };
      if(this.checkTransactionNumber) payload.transactionNumber = this.cardDetailsForm.value.transactionNumber;
    }
    if (this.paymentType === this.paymentTypeEnum[1]) {
      if (this.bankDetailsForm.invalid) {
        this.bankDetailsForm.markAllAsTouched();
        return;
      }
      payload = {
        bankDetail: {
          bankName: this.bankDetailsForm.value.bankName,
          routingNumber: this.bankDetailsForm.value.routingNumber,
          accountNumber: this.bankDetailsForm.value.accountNumber,
          nameOnAccount: this.bankDetailsForm.value.nameOnAccount,
        },
        transactionNumber: this.bankDetailsForm.value.transactionNumber,
        paymentMethod: "bank-payment",
      };
    }
    if (this.paymentType === this.paymentTypeEnum[2]) {
      if (this.chequeDetailForm.invalid) {
        this.chequeDetailForm.markAllAsTouched();
        return;
      }
      payload = {
        checkNumber: this.chequeDetailForm.value.checkNumber,
        paymentMethod: "check-payment",
      };
    }
    this.saveOptionalItems(payload);
  }
  validateTransactionNumber(isRequired: boolean) {
    const transactionNumberControl =
      this.cardDetailsForm.get("transactionNumber");
      if(isRequired){
        transactionNumberControl.setValidators(Validators.required);
        transactionNumberControl.updateValueAndValidity();
      } else {
        transactionNumberControl.clearValidators();
        transactionNumberControl.updateValueAndValidity();
      }
   
  }
  getBankDetail() {
    if (this.bankDetailsForm.value.routingNumber.length !== 9) {
      this.bankDetailsForm.get("bankName").setValue("");
      return;
    }

    this.apiService
      .get(
        endpoints.customerDashboard.checkout.getBankDetail(
          this.bankDetailsForm.value.routingNumber
        )
      )
      .pipe(debounceTime(1000))
      .pipe(distinctUntilChanged())
      .subscribe(
        (res: any) => {
          if (res?.body?.bankName) {
            this.bankDetailsForm.get("bankName").setValue(res?.body?.bankName);
            return;
          }
          this.bankDetailsForm.get("bankName").setValue("");
        },
        (err) => {
          this.bankDetailsForm.get("bankName").setValue("");
          this.apiService.alert(err.error.message, ErrorType.Error);
        }
      );
  }
  get checkTransactionNumber():boolean{
    let showTransactionNumberOrNot = this.totalPrice < 0;
    this.validateTransactionNumber(showTransactionNumberOrNot);
    return showTransactionNumberOrNot;
  }
  ngOnDestroy() {
    this.ngUnSubscribe.next();
    this.ngUnSubscribe.complete();
  }
}
